import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon, GetApp as InstallIcon, IosShare as ShareIcon } from '@mui/icons-material';

const DISMISS_KEY = 'install_prompt_dismissed_at';
const DISMISS_DAYS = 30;

const isStandalone = () =>
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true;
// iPhone/iPad (y compris iPadOS qui se declare « Macintosh » avec ecran tactile) sous Safari
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isSafari = () => /safari/i.test(navigator.userAgent) && !/crios|fxios|edgios|opios|chrome|android/i.test(navigator.userAgent);

function recentlyDismissed() {
  try {
    const t = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return t && Date.now() - t < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

// Invitation a installer l'application sur l'ecran d'accueil.
//  - Android / Chrome / Edge : le navigateur declenche `beforeinstallprompt`, on affiche notre bouton.
//  - iPhone / iPad : aucune invite automatique n'existe ; on explique le geste (Partager > Sur l'ecran d'accueil).
//  - Deja installee, ou refusee il y a moins de 30 jours : on n'affiche rien.
function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [iosHint, setIosHint] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) { setHidden(true); return undefined; }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setDeferred(null); setIosHint(false); setHidden(true); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    if (isIos() && isSafari()) setIosHint(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* sans effet */ }
    setHidden(true);
  };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* sans effet */ }
    setDeferred(null);
  };

  if (hidden || (!deferred && !iosHint)) return null;
  return (
    <Box
      role="region"
      aria-label="Installer l'application"
      sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}
    >
      <InstallIcon fontSize="small" />
      <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.3 }}>
        {deferred
          ? "Installez l'Éphéméride Duala sur votre écran d'accueil : un accès direct, comme une application."
          : (<>Pour installer l'application : touchez <ShareIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} /> <strong>Partager</strong>, puis <strong>« Sur l'écran d'accueil »</strong>.</>)}
      </Typography>
      {deferred && (
        <Button size="small" variant="contained" color="inherit" onClick={install} sx={{ textTransform: 'none', fontWeight: 700, color: 'primary.main', bgcolor: '#fff', '&:hover': { bgcolor: '#f0f0f0' } }}>
          Installer
        </Button>
      )}
      <IconButton size="small" onClick={dismiss} aria-label="Fermer" sx={{ color: 'inherit' }}><CloseIcon fontSize="small" /></IconButton>
    </Box>
  );
}

export default InstallPrompt;
